import { Text } from '@sitecore-jss/sitecore-jss-react';
import React from 'react';

import Collapse from 'core/components/Collapse';
import { Icon, ICONS } from 'core/components/Icon';
import { ICONS as EICONS } from 'core/components/Icon/svgs';
import RichText from 'core/components/RichText';
import ExperienceEditorContent from 'core/sitecore/experience-editor/ExperienceEditorContent';
import * as GS from 'core/styles/Containers';
import * as Typo from 'core/styles/Typography';
import { isJssField, JssTextType } from 'core/utils/sitecore-utils';
import HelpText from 'pods/forms/components/base/HelpText';

import * as S from './UpdatedCtrlHolder.style';

interface IProps {
	htmlId?: string;
	label?: string | JssTextType;
	errorMsg?: string;
	appendMsg?: string;
	value?: any;
	disabled?: boolean;
	startAdornment?: string;
	endAdornment?: string;
	bottomMsg?: JssTextType;
	children: React.ReactNode;
	fullWidth?: boolean;
	ctrlHolderFullWidth?: boolean;
	placeholder?: string | JssTextType;
	helpText?: string | React.ReactNode;
	customClassName?: string;
	margin?: string;
	enableButtonWithin?: boolean;
	bottomMsgWithIcon?: EICONS;
}

const UpdatedCtrlHolder = ({
	value,
	label,
	htmlId,
	errorMsg,
	appendMsg,
	disabled,
	children,
	fullWidth,
	ctrlHolderFullWidth,
	bottomMsg,
	placeholder,
	endAdornment,
	startAdornment,
	helpText,
	customClassName,
	margin,
	enableButtonWithin,
	bottomMsgWithIcon,
}: IProps) => {
	const hasError = !!errorMsg;
	const hasValue = !!value;

	const isLabelJss = isJssField(label);
	const isPlaceholderJss = isJssField(placeholder);
	const showEEcontent = isLabelJss || isPlaceholderJss;

	return (
		<S.CtrlHolder {...{ hasError, hasValue, margin }}>
			{!!helpText && <HelpText id={`${htmlId}-helpText`}>{helpText}</HelpText>}

			<S.FieldContents
				{...{
					hasError,
					startAdornment,
					endAdornment,
					fullWidth,
					customClassName,
				}}
			>
				<S.Field
					hasAppendMsg={!!appendMsg}
					ctrlHolderFullWidth={!!ctrlHolderFullWidth}
					{...{ hasError, startAdornment, endAdornment, enableButtonWithin }}
				>
					{children}
				</S.Field>

				{label && (
					<S.LegendLabel
						hasValue={hasValue && !disabled}
						disabled={disabled}
						hasError={hasError}
						htmlFor={htmlId}
					>
						{isJssField(label) ? <Text field={label as JssTextType} /> : label}
					</S.LegendLabel>
				)}

				{!!appendMsg && <RichText content={appendMsg} />}

				{htmlId && (
					<Collapse isOpen={hasError}>
						<S.ErrorMessage id={`${htmlId}-error`} role="alert">
							{errorMsg && <Icon name={ICONS.ERROR} />}
							{errorMsg && <RichText content={errorMsg} />}
						</S.ErrorMessage>
					</Collapse>
				)}
				{!!bottomMsg && (
					<>
						<S.BottomMessageContainer>
							{!!bottomMsgWithIcon && (
								<Icon name={bottomMsgWithIcon ?? ICONS.ERROR} />
							)}
							<Typo.Footnote
								isRichText
								className="bottom-msg"
								jss={bottomMsg}
								margin="4px 0 0"
							/>
						</S.BottomMessageContainer>
					</>
				)}
				{showEEcontent && (
					<ExperienceEditorContent title={`${htmlId} field`} margin="12px 0 0">
						<GS.EEFieldDefinitionList>
							{isLabelJss && (
								<>
									<dt>Label: </dt>
									<dd>
										<Text field={label as JssTextType} />
									</dd>
								</>
							)}
							{isPlaceholderJss && (
								<>
									<dt>Placeholder: </dt>
									<dd>
										<Text field={placeholder as JssTextType} />
									</dd>
								</>
							)}
						</GS.EEFieldDefinitionList>
					</ExperienceEditorContent>
				)}
			</S.FieldContents>
		</S.CtrlHolder>
	);
};

export default UpdatedCtrlHolder;
